import {announce} from '@github-ui/aria-live'
import {testIdProps} from '@github-ui/test-id-props'
import {BookIcon} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {useEffect} from 'react'

import {getInitialState} from '../helpers/initial-state'
import {getIssueFieldsOwnerType, getIssueFieldsSettingsHref} from '../helpers/issue-fields-owner-type'
import {ViewerPrivileges} from '../helpers/viewer-privileges'
import {IssueFieldResources} from '../strings'
import styles from './add-issue-fields-dialog.module.css'

type Props = {
  reason: 'all-restricted-filtered' | 'all-added' | 'no-fields-exist'
}

/**
 * Empty state shown in the Add issue fields dialog when no issue fields are available to add.
 *
 * Three variants are rendered depending on why no fields are available:
 * - When every owner field has restricted visibility ("Organization only" for an organization owner,
 *   "Restricted" for a user owner) and is therefore filtered out for a public/internal project, we
 *   explain the visibility restriction.
 * - When all available issue fields are already added to this project, we explain that state.
 * - Otherwise no issue fields exist yet, so we point the viewer at the owner's field settings.
 */
export function AddIssueFieldsEmptyState({reason}: Props) {
  const {projectOwner, loggedInUser} = getInitialState()
  const {hasAdminPermissions} = ViewerPrivileges()
  const ownerType = getIssueFieldsOwnerType()

  const isOrganization = ownerType === 'organization'

  // Organization fields live in the organization settings namespace, which any project admin can
  // reach. A user's fields live in their own personal settings, which nobody else can open, so a
  // project admin who is not the owner would otherwise be sent to their own empty settings page.
  const viewerCanManageFields = isOrganization ? hasAdminPermissions : loggedInUser?.id === projectOwner?.id
  const manageFieldsHref = getIssueFieldsSettingsHref(projectOwner)
  const showManageFieldsLink = viewerCanManageFields && !!manageFieldsHref

  const description =
    reason === 'all-restricted-filtered'
      ? IssueFieldResources.EmptyState.allRestrictedDescription
      : reason === 'all-added'
        ? IssueFieldResources.EmptyState.allAddedDescription
        : IssueFieldResources.EmptyState.noFieldsDescription

  useEffect(() => {
    announce(`${IssueFieldResources.EmptyState.heading}. ${description}`)
  }, [description])

  return (
    <Blankslate size="small" className={styles.emptyState} {...testIdProps('add-issue-fields-empty-state')}>
      <Blankslate.Visual>
        <BookIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>{IssueFieldResources.EmptyState.heading}</Blankslate.Heading>
      <Blankslate.Description>{description}</Blankslate.Description>
      {showManageFieldsLink && (
        <Blankslate.PrimaryAction href={manageFieldsHref} {...testIdProps('add-issue-fields-manage-fields-link')}>
          {IssueFieldResources.EmptyState.manageFieldsLinkText}
        </Blankslate.PrimaryAction>
      )}
    </Blankslate>
  )
}
